// src/stores/invoiceStore.js
import { create } from 'zustand';
import * as invoiceService from '../services/invoiceService';
import useNotificationStore from './notificationStore';

const useInvoiceStore = create((set, get) => ({
  // State
  invoices: [],
  currentInvoice: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },
  filters: {
    status: 'all',
    clientId: 'all',
    dateRange: {
      startDate: null,
      endDate: null
    },
    search: ''
  },

  // Actions
  setFilters: (filters) => set({ 
    filters: { ...get().filters, ...filters },
    pagination: { ...get().pagination, page: 1 } // Reset to first page when filters change
  }),

  resetFilters: () => set({ 
    filters: {
      status: 'all',
      clientId: 'all',
      dateRange: {
        startDate: null,
        endDate: null
      },
      search: ''
    },
    pagination: { ...get().pagination, page: 1 }
  }),

  setPage: (page) => set({ 
    pagination: { ...get().pagination, page } 
  }),

  // Fetch invoices with pagination and filters
  fetchInvoices: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const { filters, pagination } = get();
      
      // Build query parameters
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Add filters to params
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }
      
      if (filters.clientId && filters.clientId !== 'all') {
        params.clientId = filters.clientId;
      }
      
      if (filters.dateRange.startDate) {
        params.startDate = filters.dateRange.startDate;
      }
      
      if (filters.dateRange.endDate) {
        params.endDate = filters.dateRange.endDate;
      }
      
      if (filters.search) {
        params.search = filters.search;
      }
      
      const response = await invoiceService.getInvoices(params);
      
      set({
        invoices: response.data.invoices,
        pagination: {
          ...get().pagination,
          total: response.meta.total,
          totalPages: response.meta.totalPages
        },
        isLoading: false
      });
      
      return response.data;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      console.log('Error fetching invoices:', error);
     console.log('error', error);
    }
  },
  
  // Fetch a single invoice by ID
  fetchInvoiceById: async (id) => {
    set({ isLoading: true, error: null, currentInvoice: null });
    
    try {
      const response = await invoiceService.getInvoice(id);
      set({ currentInvoice: response.data.invoice, isLoading: false });
      return response.data.invoice;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      console.log('Error fetching invoice:', error);
     console.log('error', error);
    }
  },
  
  // Create a new invoice
  createInvoice: async (invoiceData) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.createInvoice(invoiceData);
      
      // Refresh invoices list
      await get().fetchInvoices();
      
      set({ isLoading: false });
      return response.data.invoice;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.log('Error creating invoice:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to create invoice');
      
      throw error; // Keep throwing for create operations
    }
  },
  
  // Update an existing invoice
  updateInvoice: async (id, invoiceData) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.updateInvoice(id, invoiceData);
      
      // Update current invoice if we're viewing the same one
      if (get().currentInvoice && get().currentInvoice._id === id) {
        set({ currentInvoice: response.data.invoice });
      }
      
      // Refresh invoices list
      await get().fetchInvoices();
      
      set({ isLoading: false });
      return response.data.invoice;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.log('Error updating invoice:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to update invoice');
      
      throw error; // Keep throwing for update operations
    }
  },
  
  // Delete an invoice
  deleteInvoice: async (id) => {
    set({ isLoading: true, error: null });
    
    try {
      await invoiceService.deleteInvoice(id);
      
      // Refresh invoices list
      await get().fetchInvoices();
      
      set({ isLoading: false });
      return true;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.log('Error deleting invoice:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to delete invoice');
      
      throw error; // Keep throwing for delete operations
    }
  },
  
  // Add a payment to an invoice
  addPayment: async (invoiceId, paymentData) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.addPayment(invoiceId, paymentData);
      
      // Update current invoice with new payment
      if (get().currentInvoice && get().currentInvoice._id === invoiceId) {
        set({ currentInvoice: response.data.invoice });
      }
      
      // Refresh invoices list
      await get().fetchInvoices();
      
      set({ isLoading: false });
      return response.data.invoice;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.log('Error adding payment:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to add payment');
      
      throw error; // Keep throwing for payment operations
    }
  },
  
  // Send invoice to client
  sendInvoice: async (invoiceId, emailData) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.sendInvoice(invoiceId, emailData);
      
      // Update current invoice status
      if (get().currentInvoice && get().currentInvoice._id === invoiceId) {
        set({ currentInvoice: response.data.invoice });
      }
      
      // Refresh invoices list
      await get().fetchInvoices();
      
      set({ isLoading: false });
      return response.data.invoice;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.log('Error sending invoice:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to send invoice');
      
      throw error; // Keep throwing for send operations
    }
  },
  
  // Send a reminder for an invoice
  sendReminder: async (invoiceId, reminderData) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.sendReminder(invoiceId, reminderData);
      
      // Update current invoice
      if (get().currentInvoice && get().currentInvoice._id === invoiceId) {
        set({ currentInvoice: response.data.invoice });
      }
      
      set({ isLoading: false });
      return response.data.invoice;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.log('Error sending reminder:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to send reminder');
      
      throw error; // Keep throwing for reminder operations
    }
  },
  
  // Generate invoice PDF
  generateInvoicePdf: async (invoiceId) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.generateInvoicePdf(invoiceId);
      set({ isLoading: false });
      
      // Open PDF in a new tab or trigger download
      window.open(response.data.pdfUrl, '_blank');
      
      return response.data;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.log('Error generating PDF:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to generate PDF');
      
      throw error; // Keep throwing for PDF operations
    }
  },
  
  // Approve invoice
  approveInvoice: async (invoiceId, notes = '') => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.approveInvoice(invoiceId, notes);
      
      // Update current invoice status
      if (get().currentInvoice && get().currentInvoice._id === invoiceId) {
        set({ currentInvoice: response.data.invoice });
      }
      
      // Refresh invoices list
      await get().fetchInvoices();
      
      set({ isLoading: false });
      return response.data.invoice;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.log('Error approving invoice:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to approve invoice');
      
      throw error; // Keep throwing for approve operations
    }
  },
  
  // Cancel invoice
  cancelInvoice: async (invoiceId, reason) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.cancelInvoice(invoiceId, reason);
      
      // Update current invoice status
      if (get().currentInvoice && get().currentInvoice._id === invoiceId) {
        set({ currentInvoice: response.data.invoice });
      }
      
      // Refresh invoices list
      await get().fetchInvoices();
      
      set({ isLoading: false });
      return response.data.invoice;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.log('Error cancelling invoice:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to cancel invoice');
      
      throw error; // Keep throwing for cancel operations
    }
  },
  
  // Find overdue invoices
  findOverdueInvoices: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.findOverdueInvoices();
      set({ invoices: response.data.invoices, isLoading: false });
      return response.data.invoices;
    } catch (error) {
      set({ criticalError: error.message, isLoading: false });
      console.log('Error fetching overdue invoices:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to fetch overdue invoices');
      
      // Don't throw error - return empty array
      return [];
    }
  },
  
  // Get invoice stats for a client
  getClientInvoiceStats: async (clientId) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.getClientInvoiceStats(clientId);
      set({ isLoading: false });
      return response.data.stats;
    } catch (error) {
      set({ criticalError: error.message, isLoading: false });
      console.log('Error fetching client invoice stats:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to fetch invoice statistics');
      
      // Don't throw error - return null
      return null;
    }
  },
  
  // Get payment analytics
  getPaymentAnalytics: async (params = {}) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.getPaymentAnalytics(params);
      set({ isLoading: false });
      return response.data.analytics;
    } catch (error) {
      set({ criticalError: error.message, isLoading: false });
      console.log('Error fetching payment analytics:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to fetch payment analytics');
      
      // Don't throw error - return null
      return null;
    }
  },
  
  // Create payment link
  createPaymentLink: async (invoiceId) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await invoiceService.createPaymentLink(invoiceId);
      
      // Update current invoice if we're viewing the same one
      if (get().currentInvoice && get().currentInvoice._id === invoiceId) {
        set({ 
          currentInvoice: {
            ...get().currentInvoice,
            onlinePayment: response.data.onlinePayment
          }
        });
      }
      
      set({ isLoading: false });
      return response.data.paymentLink;
    } catch (error) {
      set({ operationError: error.message, isLoading: false });
      console.log('Error creating payment link:', error);
      
      // Show error notification
      const { showError } = useNotificationStore.getState();
      showError('Failed to create payment link');
      
      throw error; // Keep throwing for payment link operations
    }
  },
  
  // Clear current invoice
  clearCurrentInvoice: () => {
    set({ currentInvoice: null });
  },
  
  // Clear error
  clearError: () => {
    set({ error: null });
  }
}));

export default useInvoiceStore;